import { useState, useRef, useEffect } from 'react';
import { services, type Service } from '@/mocks/services';

/* ─── Service Detail Modal ──────────────────────────────────────── */
function ServiceModal({ service, onClose }: { service: Service; onClose: () => void }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleClose = () => {
    setMounted(false);
    setTimeout(onClose, 320);
  };

  return (
    <div
      className="fixed inset-0 z-[999] flex items-center justify-center px-4"
      style={{
        background: `rgba(10,16,30,${mounted ? '0.78' : '0'})`,
        backdropFilter: 'blur(6px)',
        transition: 'background 0.32s ease',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="relative w-full max-w-3xl rounded-3xl overflow-hidden"
        style={{
          background: '#fff',
          opacity: mounted ? 1 : 0,
          transform: mounted ? 'translateY(0) scale(1)' : 'translateY(32px) scale(0.96)',
          transition: 'opacity 0.35s cubic-bezier(0.22,1,0.36,1), transform 0.35s cubic-bezier(0.22,1,0.36,1)',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* Hero image */}
        <div className="relative w-full h-64 md:h-80 flex-shrink-0">
          <img
            src={service.cardImage}
            alt={service.title}
            className="w-full h-full object-cover object-top"
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to bottom, rgba(10,16,30,0.18) 0%, rgba(10,16,30,0.62) 100%)' }}
          />
          {/* Category badge */}
          <span
            className="absolute top-5 left-5 text-[11px] font-bold tracking-[2.5px] uppercase px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(255,255,255,0.92)', color: '#0A1F44' }}
          >
            {service.category}
          </span>
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-5 right-5 w-9 h-9 flex items-center justify-center rounded-full cursor-pointer transition-all duration-200 hover:scale-110"
            style={{ background: 'rgba(255,255,255,0.92)', color: '#0A1F44' }}
          >
            <i className="ri-close-line text-base w-4 h-4 flex items-center justify-center" />
          </button>
          {/* Title overlay */}
          <div className="absolute bottom-5 left-5 right-5">
            <h3
              className="text-2xl md:text-3xl font-bold text-white leading-tight"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              {service.title}
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="p-6 md:p-8">
          {/* Description */}
          <p
            className="text-[15px] leading-relaxed mb-8"
            style={{ color: 'rgba(10,31,68,0.72)', fontFamily: "'Inter', sans-serif" }}
          >
            {service.fullDesc}
          </p>

          {/* Highlights */}
          <div className="mb-8">
            <h4
              className="text-xs font-bold tracking-[2.5px] uppercase mb-4"
              style={{ color: 'rgba(10,31,68,0.38)' }}
            >
              What&apos;s Included
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {service.highlights.map((h, i) => (
                <div
                  key={h}
                  className="flex items-center gap-3 px-4 py-3 rounded-xl"
                  style={{
                    background: 'rgba(245,247,252,1)',
                    border: '1px solid rgba(10,31,68,0.07)',
                    animation: 'highlightIn 0.4s ease both',
                    animationDelay: `${i * 55}ms`,
                  }}
                >
                  <span
                    className="w-5 h-5 flex items-center justify-center rounded-full flex-shrink-0"
                    style={{ background: 'rgba(59,130,246,0.12)' }}
                  >
                    <i className="ri-check-line text-[11px] w-3 h-3 flex items-center justify-center" style={{ color: '#3B82F6' }} />
                  </span>
                  <span className="text-[13px] font-medium" style={{ color: '#0A1F44' }}>{h}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div>
            <h4
              className="text-xs font-bold tracking-[2.5px] uppercase mb-3"
              style={{ color: 'rgba(10,31,68,0.38)' }}
            >
              Technologies
            </h4>
            <div className="flex flex-wrap gap-2">
              {service.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full text-[11px] font-semibold"
                  style={{
                    background: 'rgba(59,130,246,0.07)',
                    border: '1px solid rgba(59,130,246,0.18)',
                    color: 'rgba(30,58,138,0.78)',
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes highlightIn {
          from { opacity: 0; transform: translateX(-8px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}

/* ─── Service Card ──────────────────────────────────────────────── */
function ServiceCard({ service, index, onExplore }: { service: Service; index: number; onExplore: (s: Service) => void }) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  const ENTRY = ['translateY(44px)', 'translateX(-40px)', 'translateX(40px)', 'translateY(-44px)', 'translateY(44px)', 'translateX(-40px)', 'scale(0.84)'];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={cardRef}
      className="group flex flex-col rounded-2xl overflow-hidden cursor-default"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : ENTRY[index % ENTRY.length],
        transition: `opacity 0.72s ease ${index * 70}ms, transform 0.72s cubic-bezier(0.22,1,0.36,1) ${index * 70}ms`,
        background: '#fff',
        border: '1px solid rgba(10,31,68,0.08)',
      }}
    >
      {/* Image */}
      <div className="relative overflow-hidden h-48 flex-shrink-0">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to bottom, transparent 30%, rgba(10,16,30,0.52) 100%)' }}
        />
        <span
          className="absolute top-3 left-3 text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full"
          style={{ background: 'rgba(255,255,255,0.9)', color: '#0A1F44' }}
        >
          {service.category}
        </span>
      </div>

      {/* Body */}
      <div className="p-5 flex-1 flex flex-col">
        <h3
          className="text-[15px] font-semibold mb-2 text-[#0A1F44]"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          {service.title}
        </h3>
        <p className="text-[12px] leading-relaxed flex-1 mb-5" style={{ color: 'rgba(10,31,68,0.58)' }}>
          {service.shortDesc}
        </p>

        {/* Tags preview */}
        <div className="flex flex-wrap gap-1.5 mb-5">
          {service.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="px-2 py-0.5 rounded-full text-[10px] font-medium"
              style={{ background: 'rgba(10,31,68,0.05)', color: 'rgba(10,31,68,0.55)', border: '1px solid rgba(10,31,68,0.09)' }}
            >
              {tag}
            </span>
          ))}
          {service.tags.length > 3 && (
            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: 'rgba(10,31,68,0.05)', color: 'rgba(10,31,68,0.4)' }}>
              +{service.tags.length - 3}
            </span>
          )}
        </div>

        {/* Explore button */}
        <button
          onClick={() => onExplore(service)}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-[13px] font-semibold cursor-pointer transition-all duration-200 hover:opacity-90 group-hover:shadow-sm"
          style={{
            background: 'linear-gradient(135deg, #0A1F44 0%, #1E3A6E 100%)',
            color: '#fff',
          }}
        >
          Explore Service
          <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center transition-transform duration-200 group-hover:translate-x-0.5" />
        </button>
      </div>
    </div>
  );
}

/* ─── Services Section ──────────────────────────────────────────── */
export default function Services() {
  const titleRef = useRef<HTMLDivElement>(null);
  const [titleVisible, setTitleVisible] = useState(false);
  const [activeService, setActiveService] = useState<Service | null>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setTitleVisible(true); observer.disconnect(); } },
      { threshold: 0.1 }
    );
    if (titleRef.current) observer.observe(titleRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <section
        id="services"
        className="relative overflow-hidden"
        style={{ background: '#FAFBFF' }}
      >
        {/* Top edge line */}
        <div
          className="absolute top-0 left-0 w-full h-px pointer-events-none"
          style={{ background: 'linear-gradient(to right, transparent, rgba(10,31,68,0.12), transparent)' }}
        />

        {/* Dot texture */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(10,31,68,0.045) 1px, transparent 1px)',
            backgroundSize: '36px 36px',
          }}
        />

        {/* Ambient blobs */}
        <div
          className="absolute -top-48 -left-48 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(245,158,11,0.04) 0%, transparent 70%)' }}
        />
        <div
          className="absolute -bottom-48 -right-48 w-[600px] h-[600px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(10,31,68,0.04) 0%, transparent 70%)' }}
        />

        <div className="relative z-10 py-24 md:py-32">
          <div className="max-w-[1300px] mx-auto px-6 md:px-12">

            {/* ── Header ──────────────────────────────────────── */}
            <div
              ref={titleRef}
              className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16"
              style={{
                opacity: titleVisible ? 1 : 0,
                transform: titleVisible ? 'none' : 'translateY(28px)',
                transition: 'opacity 0.8s ease, transform 0.8s ease',
              }}
            >
              <div>
                <span className="inline-flex items-center gap-2 text-[#0A1F44]/45 text-xs font-bold tracking-[3.5px] uppercase mb-4">
                  <span className="w-6 h-px bg-[#0A1F44]/30" />
                  Our Capabilities
                </span>
                <h2
                  className="text-4xl md:text-[50px] font-bold text-[#0A1F44] leading-[1.13]"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  What We{' '}
                  <span style={{ color: '#D97706' }}>
                    Build For You
                  </span>
                </h2>
              </div>
              <p className="text-[#0A1F44]/50 text-[15px] leading-relaxed max-w-sm lg:text-right">
                End-to-end AI and automation services that transform how your business operates — from smart apps to intelligent industrial systems.
              </p>
            </div>

            {/* ── Cards grid ──────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {services.map((service, i) => (
                <ServiceCard
                  key={service.id}
                  service={service}
                  index={i}
                  onExplore={setActiveService}
                />
              ))}
            </div>



          </div>
        </div>

        {/* Bottom edge line */}
        <div
          className="w-full h-px"
          style={{ background: 'linear-gradient(to right, transparent, rgba(10,31,68,0.1) 20%, rgba(10,31,68,0.1) 80%, transparent)' }}
        />
      </section>

      {/* ── Modal ─────────────────────────────────────────────── */}
      {activeService && (
        <ServiceModal
          service={activeService}
          onClose={() => setActiveService(null)}
        />
      )}
    </>
  );
}
